import { cn, tv } from "@utils/tw"
import * as Icons from "@components/atoms/Icons"
import { Link } from "eddev/routing"
import { ComponentPropsWithoutRef } from "react"

const colorVariants = {
  yellow: "yellow",
  black: "black",
  white: "white",
  whiteNoHover: "whiteNoHover",
  grey: "grey",
  outline: "outline",
  outlineReversed: "outlineReversed",
  bgColor: "$headerBackground",

  red: "red",
  warning: "warning",
  amber: "amber",
  green: "green",
  altYellow: "altYellow",
}

const iconStyleVariants = {
  left: "left",
  right: "right",
}

export type ChipT = {
  href?: string
  target?: "_blank"
  icon?: keyof typeof Icons
  color: keyof typeof colorVariants
  iconStyle?: keyof typeof iconStyleVariants
  children?: any
  interactive?: boolean
  onClick?: () => void
}

export function Chip(props: ChipT) {
  const { href, target, icon, color, iconStyle, children, onClick } = props
  const Comp = href ? Link : "button"

  const leftIcon = iconStyle === "left" && icon ? Icons[icon] : null
  const rightIcon = iconStyle === "right" && icon ? Icons[icon] : null

  const interactive = !!props.interactive || !!props.onClick || !!props.href

  return (
    <Comp
      className={cn("chip", chipStyles({ color, interactive }))}
      href={href}
      target={target ?? ""}
      onClick={onClick}
    >
      {leftIcon} {children ? <ChipSpan>{children}</ChipSpan> : null} {rightIcon}
    </Comp>
  )
}

export const chipStyles = tv({
  base: [
    "cursor-pointer flex items-center justify-center text-center",
    "transition-colors ease-movement duration-300 no-underline",
    "type-label-caps h-[20px] rounded-[10px] px-2",
    "[&_svg]:w-[16px] [&_svg]:h-[16px]",
    "lg:px-1",
    "[&:has(a)]:pointer-events-auto [&:is(a)]:pointer-events-auto",
    !env.admin && "pointer-events-none",
  ],
  variants: {
    color: {
      yellow:
        "bg-yellow !text-black hover:bg-hover-yellow active:bg-active-yellow disabled:bg-disabled-yellow disabled:!text-grey",
      black: "bg-black !text-white hover:bg-dark-grey active:bg-white active:!text-black disabled:bg-grey",
      white: "bg-bg !text-black hover:bg-grey active:bg-black active:!text-white disabled:bg-grey disabled:!text-white",
      whiteNoHover: "bg-bg !text-black cursor-default",
      grey: "bg-light-grey !text-black hover:bg-grey active:bg-black active:!text-white disabled:bg-grey disabled:!text-mid-grey",
      outline:
        "bg-transparent !text-dark-grey ring-1 ring-inset ring-dark-grey hover:bg-dark-grey hover:ring-dark-grey hover:!text-white active:bg-white active:ring-white active:!text-white disabled:bg-transparent disabled:ring-mid-grey disabled:!text-mid-grey",
      outlineReversed:
        "bg-transparent !text-white ring-1 ring-inset ring-white hover:bg-grey hover:ring-dark-grey hover:!text-black active:bg-white active:ring-white active:!text-black disabled:bg-transparent disabled:ring-mid-grey disabled:!text-mid-grey",
      // Intent per the original color token map ($headerBackground) — this
      // variant was declared in the type but had no actual style
      // implementation, so it silently rendered unstyled everywhere it's
      // used (Breadcrumbs.tsx).
      bgColor: "bg-header-background !text-black",
      red: "bg-red-light !text-red",
      warning: "bg-red !text-white",
      amber: "bg-amber-light !text-amber",
      green: "bg-green-light !text-green",
      altYellow: "bg-alt-yellow-light !text-alt-yellow",
    },
    interactive: {
      true: "!pointer-events-auto",
    },
  },
  defaultVariants: {
    color: "grey",
  },
})

function ChipSpan({ className, ...props }: ComponentPropsWithoutRef<"span">) {
  return <span className={cn("px-[6px]", className)} {...props} />
}
